import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@material-ui/core";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { fetchSuccessProgress } from "../../reduxSlices/successProgressSlice";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28"];
const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  name,
  index,
  value,
}) => {
  // Do not draw labels for zero slices to avoid overlapping
  if (!value || value <= 0) return null;

  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={COLORS[index % COLORS.length]}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      style={{ fontSize: 12 }}
    >
      {name}
    </text>
  );
};

const SuccessProgress = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.successProgress);
  const [selectedPlanId, setSelectedPlanId] = useState("");

  useEffect(() => {
    const studentId = window.localStorage.getItem("studentId");
    if (studentId) {
      dispatch(fetchSuccessProgress(studentId));
    }
  }, [dispatch]);

  // Normalize data: API returns an array of records
  const records = Array.isArray(data) ? data : data?.value || [];

  // Build degree plan options using ses_certificationprogramname for label
  const degreeOptions = records
    .map((item) => {
      const degreePlanId = item.ses_programrequirementsrecordid;
      const degreePlanName = item.ses_certificationprogramname;
      return degreePlanId
        ? { id: degreePlanId, name: degreePlanName || "Degree Plan" }
        : null;
    })
    .filter(Boolean);

  useEffect(() => {
    if (!selectedPlanId && degreeOptions.length > 0) {
      setSelectedPlanId(degreeOptions[0].id);
    }
  }, [degreeOptions, selectedPlanId]);

  const currentRecord =
    records.find(
      (item) => item.ses_programrequirementsrecordid === selectedPlanId,
    ) || records[0];

  const totalCreditsNeeded =
    currentRecord?.ses_certificateprogramplancredits ??
    currentRecord?.ses_totalrequiredcredits ??
    0;
  const totalCreditsEarned =
    (currentRecord?.ses_earnedcredits ?? 0) +
    (currentRecord?.ses_transfercredits ?? 0);
  const totalCreditsRemaining = totalCreditsNeeded - totalCreditsEarned;
  const earned = totalCreditsEarned;
  const inProgress = currentRecord?.ses_availablecredits ?? 0;
  const remainingRaw = totalCreditsNeeded - (earned + inProgress);
  const remaining = remainingRaw < 0 ? 0 : remainingRaw;

  let chartData = [
    { name: "Completed", value: earned },
    { name: "In Progress", value: inProgress },
    { name: "Remaining", value: remaining },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0].payload;
      return (
        <div
          style={{ background: "#fff", border: "1px solid #ccc", padding: 8 }}
        >
          <div>
            <strong>Status:</strong> {name}
          </div>
          <div>
            <strong>Count:</strong> {value}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      elevation={3}
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 1100,
        marginBottom: 0,
      }}
    >
      <CardHeader title="Degree Progress" />
      <CardContent
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <div>Error: {error}</div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {degreeOptions.length > 1 && (
              <FormControl
                variant="outlined"
                size="small"
                style={{ marginBottom: 16, minWidth: 220 }}
              >
                <InputLabel id="degree-plan-label">
                  Degree Plan
                </InputLabel>
                <Select
                  labelId="degree-plan-label"
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  label="Degree Plan"
                >
                  {degreeOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

              <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                width: "100%",
                maxWidth: 850,
                gap: 40,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  minWidth: 220,
                  gap: 16,
                }}
              >
                <div
                  style={{
                    padding: 16,
                    background: "#f8f9fc",
                    borderRadius: 12,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      marginBottom: 12,
                    }}
                  >
                    Degree Progress Summary
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Total Credits Needed
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#1976d2" }}>
                      {totalCreditsNeeded}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Total Credits Earned
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#0088FE" }}>
                      {totalCreditsEarned}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Total Credits Remaining
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#FFBB28" }}>
                      {totalCreditsRemaining < 0 ? 0 : totalCreditsRemaining}
                    </div>
                  </div>
                </div>
              </div>
              <PieChart width={400} height={300}>
                <Pie
                  data={chartData}
                  cx={200}
                  cy={150}
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={renderCustomizedLabel}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SuccessProgress;
